import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import SearchSelectBox from '@components/SearchSelectBox/SearchSelectBox';
import { IDietarySupplementTableData } from '@interfaces/supplements';
import { handleSearchSupplements } from '@store/features/inquiryDraft/supplementSearch/actions';
import {
  supplementSearchIsLoadingSelector,
  supplementSearchPaginationSelector,
  supplementSearchSelector,
} from '@store/features/inquiryDraft/supplementSearch/selectors';
import { removeDietarySupplementSearch } from '@store/features/inquiryDraft/supplementSearch/slice';
import useDispatch from '@store/hooks/useDispatch';

import { ISupplementSearchSelectBoxProps } from './SupplementSearchSelectBox.interface';

const SupplementSearchSelectBox: FC<ISupplementSearchSelectBoxProps> = ({ selected, onSelect }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchValue, setSearchValue] = useState<string>('');

  const dietarySupplementSearch = useSelector(supplementSearchSelector);
  const dietarySupplementPagination = useSelector(supplementSearchPaginationSelector);
  const dietarySupplementSearchLoading = useSelector(supplementSearchIsLoadingSelector);

  const handleSearchDietarySupplement = useCallback(
    (value: string): void => {
      const search = value.trim();

      setSearchValue(search);

      dispatch(removeDietarySupplementSearch());
      dispatch(handleSearchSupplements({ search, page: 1 }));
    },
    [dispatch],
  );

  const handleLoadMore = useCallback(() => {
    const page = dietarySupplementPagination?.page || 1;
    const pageCount = dietarySupplementPagination?.pageCount || 0;

    if ((page === pageCount && pageCount > 1) || searchValue.length < 3) {
      return;
    }

    dispatch(
      handleSearchSupplements({
        search: searchValue,
        page: page < pageCount ? page + 1 : page,
      }),
    );
  }, [
    dispatch,
    dietarySupplementPagination?.page,
    dietarySupplementPagination?.pageCount,
    searchValue,
  ]);

  return (
    <SearchSelectBox<IDietarySupplementTableData>
      title={t('dietarySupplementInquiry.supplementName')}
      items={dietarySupplementSearch}
      selected={selected}
      onSelect={onSelect}
      onSearch={handleSearchDietarySupplement}
      onLoadMore={handleLoadMore}
      isLoading={dietarySupplementSearchLoading}
      isMedicationSearch={false}
      searchValue={searchValue}
    />
  );
};

export default SupplementSearchSelectBox;
